<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <div class="ds-tabs">
                    <button
                        type="button"
                        class="ds-tab"
                        :class="{ 'ds-tab--active': activeTab === 'types' }"
                        @click="setTab('types')"
                    >
                        Types
                    </button>
                    <button
                        type="button"
                        class="ds-tab"
                        :class="{ 'ds-tab--active': activeTab === 'providers' }"
                        @click="setTab('providers')"
                    >
                        Providers
                    </button>
                </div>
                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" :disabled="isExportExcel" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        {{ activeTab === "types" ? "Add type" : "Add provider" }}
                    </button>
                </div>
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div v-if="loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="!loading && items.length === 0" class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">💾</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">
                        No {{ activeTab === "types" ? "digital holding types" : "providers" }} found
                    </h2>
                    <p class="text-gray-500 mb-4">Add records for digital holdings.</p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        {{ activeTab === "types" ? "Add type" : "Add provider" }}
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th class="ds-th w-12">#</th>
                                <th v-if="activeTab === 'types'" class="ds-th">Key</th>
                                <th class="ds-th">{{ activeTab === "types" ? "Label" : "Name" }}</th>
                                <th class="ds-th">Total</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                <td class="ds-td text-gray-400 font-medium">{{ rowNumber(index) }}</td>
                                <td v-if="activeTab === 'types'" class="ds-td font-mono text-xs">
                                    {{ item.key || "—" }}
                                </td>
                                <td class="ds-td font-medium">
                                    {{ activeTab === "types" ? item.label || "—" : item.name || "—" }}
                                </td>
                                <td class="ds-td">{{ formatCount(item.total) }}</td>
                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn" @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDelete" type="button" title="Delete" class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ formTitle }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">{{ entity }}</p>
                    </div>
                    <div class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <template v-if="activeTab === 'types'">
                            <div>
                                <label class="ds-label">Key <span class="text-red-500">*</span></label>
                                <input v-model="form.key" type="text" class="ds-input" placeholder="ebooks" />
                            </div>
                            <div>
                                <label class="ds-label">Label <span class="text-red-500">*</span></label>
                                <input v-model="form.label" type="text" class="ds-input" placeholder="eBooks" />
                            </div>
                        </template>
                        <template v-else>
                            <div class="sm:col-span-2">
                                <label class="ds-label">Name <span class="text-red-500">*</span></label>
                                <input v-model="form.name" type="text" class="ds-input" placeholder="Provider name" />
                            </div>
                        </template>
                        <div>
                            <label class="ds-label">Total</label>
                            <input v-model="form.total" type="number" min="0" class="ds-input" placeholder="0" />
                        </div>
                    </div>
                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">Cancel</button>
                        <base-button type="button" :disabled="saving" class="bg-teal-700 hover:bg-teal-800 text-white" @click="saveForm">
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import exportManagerMixin from "../../facility/modules/exportManagerMixin.js";

const TYPES_QUERY = gql`
    query LibraryDigitalHoldingTypes($first: Int!, $page: Int!, $entity: String) {
        libraryDigitalHoldingTypes(first: $first, page: $page, entity: $entity) {
            error
            error_code
            message
            data {
                id
                entity
                key
                label
                total
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const PROVIDERS_QUERY = gql`
    query LibraryDigitalHoldingProviders($first: Int!, $page: Int!, $entity: String) {
        libraryDigitalHoldingProviders(first: $first, page: $page, entity: $entity) {
            error
            error_code
            message
            data {
                id
                entity
                name
                total
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const CREATE_TYPE = gql`
    mutation CreateLibraryDigitalHoldingType($input: LibraryDigitalHoldingTypeInput!) {
        createLibraryDigitalHoldingType(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_TYPE = gql`
    mutation UpdateLibraryDigitalHoldingType($id: ID!, $input: LibraryDigitalHoldingTypeInput!) {
        updateLibraryDigitalHoldingType(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_TYPE = gql`
    mutation DeleteLibraryDigitalHoldingType($id: ID!) {
        deleteLibraryDigitalHoldingType(id: $id) {
            error
            error_code
            message
        }
    }
`;

const CREATE_PROVIDER = gql`
    mutation CreateLibraryDigitalHoldingProvider($input: LibraryDigitalHoldingProviderInput!) {
        createLibraryDigitalHoldingProvider(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_PROVIDER = gql`
    mutation UpdateLibraryDigitalHoldingProvider($id: ID!, $input: LibraryDigitalHoldingProviderInput!) {
        updateLibraryDigitalHoldingProvider(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_PROVIDER = gql`
    mutation DeleteLibraryDigitalHoldingProvider($id: ID!) {
        deleteLibraryDigitalHoldingProvider(id: $id) {
            error
            error_code
            message
        }
    }
`;

function emptyTypeForm() {
    return { key: "", label: "", total: "0" };
}

function emptyProviderForm() {
    return { name: "", total: "0" };
}

function formIntegerToValue(value) {
    if (value === null || value === undefined || value === "") return 0;
    var parsed = parseInt(String(value), 10);
    if (isNaN(parsed) || parsed < 0) return 0;
    return parsed;
}

export default {
    name: "DigitalHoldingsModule",
    mixins: [exportManagerMixin],
    components: { Pagination, TableShimmer },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, default: "" },
    },
    emits: ["changed"],
    data() {
        return {
            activeTab: "types",
            items: [],
            paginatorInfo: null,
            loading: false,
            isExportExcel: false,
            perPage: 25,
            page: 1,
            showFormModal: false,
            editingId: null,
            saving: false,
            form: emptyTypeForm(),
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("library-digital-holdings-create");
        },
        canUpdate() {
            return this.hasPermission("library-digital-holdings-update");
        },
        canDelete() {
            return this.hasPermission("library-digital-holdings-delete");
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        tableHeaders() {
            if (this.activeTab === "types") {
                return ["#", "Key", "Label", "Total", "Actions"];
            }
            return ["#", "Name", "Total", "Actions"];
        },
        formTitle() {
            if (this.activeTab === "types") {
                return this.editingId ? "Edit digital holding type" : "Add digital holding type";
            }
            return this.editingId ? "Edit digital holding provider" : "Add digital holding provider";
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1,
                lastItem: pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total),
            };
        },
    },
    created() {
        this.loadList();
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        formatCount(value) {
            if (value === null || value === undefined || value === "") return "—";
            return value;
        },
        rowNumber(index) {
            if (!this.paginatorInfo) return index + 1;
            return (this.paginatorInfo.firstItem || 1) + index;
        },
        setTab(tab) {
            if (this.activeTab === tab) return;
            this.activeTab = tab;
            this.page = 1;
            this.closeForm();
            this.loadList();
        },
        setPage(page) {
            this.page = page;
            this.loadList();
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadList() {
            var self = this;
            var isTypes = this.activeTab === "types";
            this.loading = true;
            this.$apollo
                .query({
                    query: isTypes ? TYPES_QUERY : PROVIDERS_QUERY,
                    variables: { first: this.perPage, page: this.page, entity: this.entity },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload = isTypes
                        ? data && data.libraryDigitalHoldingTypes
                        : data && data.libraryDigitalHoldingProviders;
                    if (payload && !payload.error) {
                        self.items = payload.data || [];
                        self.paginatorInfo = payload.paginatorInfo || null;
                    } else {
                        self.items = [];
                        self.paginatorInfo = null;
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Failed to load digital holdings",
                        });
                    }
                })
                .catch(function (err) {
                    self.items = [];
                    self.paginatorInfo = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load digital holdings",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.form = this.activeTab === "types" ? emptyTypeForm() : emptyProviderForm();
            this.showFormModal = true;
        },
        openEdit(item) {
            this.editingId = item.id;
            if (this.activeTab === "types") {
                this.form = {
                    key: item.key || "",
                    label: item.label || "",
                    total: item.total === null || item.total === undefined ? "0" : String(item.total),
                };
            } else {
                this.form = {
                    name: item.name || "",
                    total: item.total === null || item.total === undefined ? "0" : String(item.total),
                };
            }
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.editingId = null;
            this.form = this.activeTab === "types" ? emptyTypeForm() : emptyProviderForm();
        },
        buildInput() {
            if (this.activeTab === "types") {
                return {
                    entity: this.entity,
                    key: this.form.key || "",
                    label: this.form.label || "",
                    total: formIntegerToValue(this.form.total)
                };
            }
            return {
                entity: this.entity,
                name: this.form.name || "",
                total: formIntegerToValue(this.form.total)
            };
        },
        saveForm() {
            var self = this;
            var isTypes = this.activeTab === "types";
            if (isTypes) {
                if (!this.form.key || !String(this.form.key).trim()) {
                    this.toastAlert({ icon: "warning", title: "Key is required" });
                    return;
                }
                if (!this.form.label || !String(this.form.label).trim()) {
                    this.toastAlert({ icon: "warning", title: "Label is required" });
                    return;
                }
            } else if (!this.form.name || !String(this.form.name).trim()) {
                this.toastAlert({ icon: "warning", title: "Name is required" });
                return;
            }

            this.saving = true;
            var input = this.buildInput();
            var request;
            if (isTypes) {
                request = this.editingId
                    ? this.$apollo.mutate({
                          mutation: UPDATE_TYPE,
                          variables: { id: this.editingId, input: input },
                      })
                    : this.$apollo.mutate({
                          mutation: CREATE_TYPE,
                          variables: { input: input },
                      });
            } else {
                request = this.editingId
                    ? this.$apollo.mutate({
                          mutation: UPDATE_PROVIDER,
                          variables: { id: this.editingId, input: input },
                      })
                    : this.$apollo.mutate({
                          mutation: CREATE_PROVIDER,
                          variables: { input: input },
                      });
            }

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = null;
                    if (isTypes) {
                        payload = self.editingId
                            ? data && data.updateLibraryDigitalHoldingType
                            : data && data.createLibraryDigitalHoldingType;
                    } else {
                        payload = self.editingId
                            ? data && data.updateLibraryDigitalHoldingProvider
                            : data && data.createLibraryDigitalHoldingProvider;
                    }
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        confirmDelete(item) {
            var self = this;
            if (!this.$swal) return;
            var label =
                this.activeTab === "types"
                    ? item.label || item.key || "—"
                    : item.name || "—";
            this.$swal
                .fire({
                    title: "Delete this record?",
                    html:
                        "<div class='text-left text-sm text-gray-600'><div><strong>Item:</strong> " +
                        label +
                        "</div></div>",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, delete",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) self.deleteItem(item);
                });
        },
        deleteItem(item) {
            var self = this;
            var isTypes = this.activeTab === "types";
            this.$apollo
                .mutate({
                    mutation: isTypes ? DELETE_TYPE : DELETE_PROVIDER,
                    variables: { id: item.id },
                })
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = isTypes
                        ? data && data.deleteLibraryDigitalHoldingType
                        : data && data.deleteLibraryDigitalHoldingProvider;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Deleted",
                        });
                        if (self.items.length === 1 && self.page > 1) self.page = self.page - 1;
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Delete failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Delete failed",
                    });
                });
        },
        exportFileName() {
            return this.activeTab === "types"
                ? "ADPOLY_Library_Digital_Types"
                : "ADPOLY_Library_Digital_Providers";
        },
        exportSheetName() {
            return this.activeTab === "types" ? "Digital Types" : "Digital Providers";
        },
        _getExportQuery: function () {
            if (this.activeTab === "types") {
                return { query: TYPES_QUERY, root: "libraryDigitalHoldingTypes" };
            }
            return { query: PROVIDERS_QUERY, root: "libraryDigitalHoldingProviders" };
        },
        _snapshotExportVariables: function () {
            var entity = this.entity;
            return function (page, limit) {
                return { first: limit, page: page, entity: entity };
            };
        },
        mapExportRow(item) {
            if (this.activeTab === "types") {
                return {
                    Key: item.key || "",
                    Label: item.label || "",
                    Total: item.total === null || item.total === undefined ? "" : item.total,
                };
            }
            return {
                Name: item.name || "",
                Total: item.total === null || item.total === undefined ? "" : item.total,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}
.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}
@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}
.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
}
.ds-tabs {
    display: inline-flex;
    gap: 0.35rem;
    padding: 0.25rem;
    background: #f1f5f9;
    border-radius: 0.75rem;
}
.ds-tab {
    border: none;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
    padding: 0.45rem 0.9rem;
    border-radius: 0.55rem;
    cursor: pointer;
}
.ds-tab--active {
    background: #0f766e;
    color: #ffffff;
}
.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}
.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.55rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
}
.ds-btn--primary {
    background: #0f766e;
    color: #ffffff;
    border: 1px solid #0f766e;
}
.ds-btn--ghost {
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
}
.ds-export-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
}
.ds-export-btn:disabled {
    opacity: 0.6;
    pointer-events: none;
}
.ds-export-btn__icon {
    color: #059669;
}
.ds-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
}
.ds-input:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
}
.ds-table-shell {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #ffffff;
}
.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: #0f766e;
}
.ds-th:first-child {
    border-top-left-radius: 0.7rem;
}
.ds-th:last-child {
    border-top-right-radius: 0.7rem;
}
.ds-row:nth-child(even) {
    background: #f0fdfa;
}
.ds-row:hover {
    background: #ccfbf1;
}
.ds-td {
    padding: 0.7rem 1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
}
.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: #f3f4f6;
    margin-left: 0.35rem;
}
.ds-icon-btn--danger:hover {
    background: #fee2e2;
}
.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}
.ds-form__header {
    padding: 1.25rem 1.5rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
}
.ds-form__body {
    padding: 1.25rem 1.5rem;
}
.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
}
.ds-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
